import { Button, Input, Modal } from '@components/common';
import React, { useEffect, useMemo, useState } from 'react';
import {
  AccessControlRecord,
  AccessPolicySummary,
} from '../../../types/accessControl';
import { AccessControlChecklist } from './AccessControlChecklist';

interface PolicyEditorModalProps {
  isOpen: boolean;
  isSaving: boolean;
  controls: AccessControlRecord[];
  policy: AccessPolicySummary | null;
  onClose: () => void;
  onSubmit: (payload: {
    id?: string;
    name: string;
    description: string;
    controlIds: string[];
  }) => Promise<void>;
}

export const PolicyEditorModal: React.FC<PolicyEditorModalProps> = ({
  isOpen,
  isSaving,
  controls,
  policy,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedControlIds, setSelectedControlIds] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setName(policy?.name || '');
    setDescription(policy?.description || '');
    setSelectedControlIds(policy?.control_ids || []);
  }, [isOpen, policy]);

  const controlCountLabel = useMemo(
    () =>
      `${selectedControlIds.length} control${selectedControlIds.length === 1 ? '' : 's'} selected`,
    [selectedControlIds.length],
  );

  const handleToggleControl = (controlId: string) => {
    setSelectedControlIds((current) =>
      current.includes(controlId)
        ? current.filter((id) => id !== controlId)
        : [...current, controlId],
    );
  };

  const handleSubmit = async () => {
    await onSubmit({
      id: policy?.id,
      name,
      description,
      controlIds: selectedControlIds,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={policy ? 'Update policy' : 'Create policy'}
      footer={
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={!name.trim()}
          >
            {policy ? 'Save changes' : 'Create policy'}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="grid gap-4 md:grid-cols-2">
          <Input
            label="Policy name"
            value={name}
            onChange={(value) => setName(String(value))}
            placeholder="Cashier front desk"
          />
          <div className="rounded-2xl border border-gray-200 bg-app-bg/40 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-gray-500">
              Selection
            </p>
            <p className="mt-2 text-sm font-semibold text-black">
              {controlCountLabel}
            </p>
            <p className="mt-1 text-xs text-gray-600">
              Choose the controls this policy should grant to the selected role.
            </p>
          </div>
        </div>

        <Input
          label="Description"
          value={description}
          onChange={(value) => setDescription(String(value))}
          placeholder="Optional note to help the team understand when to use this policy"
        />

        <AccessControlChecklist
          controls={controls}
          selectedControlIds={selectedControlIds}
          onToggleControl={handleToggleControl}
        />
      </div>
    </Modal>
  );
};
